import { useState } from "react";
import { toast, Toaster } from "sonner";
import {
  Play, Rocket, Download, ChevronDown, ChevronRight,
  AlertTriangle, CheckCircle2, Shield, Loader2,
} from "lucide-react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";

/**
 * Phase 24.1 — Workflow Repair console (super-admin only).
 *
 * Flow:
 *   1. operator clicks Preview → GET /api/admin/workflow-repair/preview
 *      (zero DB writes, returns 3 buckets)
 *   2. operator un-ticks any ref they want to exclude
 *   3. operator ticks the "I have reviewed" confirmation
 *   4. Apply hits POST /api/admin/workflow-repair/apply which creates
 *      a timestamped JSON backup BEFORE any mutation
 *   5. backup can be downloaded via /api/admin/workflow-repair/backup/<file>
 */
const BUCKET_META = {
  A: {
    title: "Bucket A — current_approvers mismatch",
    tint: "border-[#2d6b6b] bg-[#f1faf6]",
    chip: "bg-[#dcefe6] text-[#1f3f36]",
    actionable: true,
    describe: "Resync approvers to match the current stage. No stage move.",
  },
  B: {
    title: "Bucket B — Service v11 stuck at service_po_review",
    tint: "border-[#b48a45] bg-[#fcf5e5]",
    chip: "bg-[#f1e2bd] text-[#5a4215]",
    actionable: true,
    describe: "Move stage to stores_po_review + reassign approvers.",
  },
  C: {
    title: "Bucket C — Transport v22 skipped acknowledgement (manual review)",
    tint: "border-[#b3261e] bg-[#fbeae8]",
    chip: "bg-[#f1c7c1] text-[#5a1013]",
    actionable: false,
    describe:
      "Human review required — use 'Amend decision' on each form to " +
      "pick the correct replay stage.",
  },
};

export default function WorkflowRepairPage() {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [ticked, setTicked] = useState({});                // ref_code -> bool
  const [confirmed, setConfirmed] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [openBucket, setOpenBucket] = useState({ A: true, B: true, C: false });

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const r = await api.get("/admin/workflow-repair/preview");
      setPreview(r.data);
      // Default: tick every actionable (A + B) ref.
      const init = {};
      r.data.records.forEach((rec) => {
        if (rec.bucket !== "C") init[rec.ref_code] = true;
      });
      setTicked(init);
      setConfirmed(false);
      toast.success(
        `Preview complete — ${r.data.counts.total} record(s) classified`,
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const tickedActionableCount = () => {
    if (!preview) return 0;
    return preview.records.filter(
      (r) => r.bucket !== "C" && ticked[r.ref_code],
    ).length;
  };

  const runApply = async () => {
    if (!preview || !confirmed) return;
    const a = preview.records
      .filter((r) => r.bucket === "A" && ticked[r.ref_code])
      .map((r) => r.ref_code);
    const b = preview.records
      .filter((r) => r.bucket === "B" && ticked[r.ref_code])
      .map((r) => r.ref_code);
    if (a.length === 0 && b.length === 0) {
      toast.error("Tick at least one record before applying.");
      return;
    }
    setApplying(true);
    try {
      const r = await api.post("/admin/workflow-repair/apply", {
        bucket_a_refs: a,
        bucket_b_refs: b,
        bucket_c_refs: [],
        confirm: true,
      });
      setApplyResult(r.data);
      toast.success(
        `Repair applied — backup saved as ${r.data.backup_file}`,
      );
      // Re-run preview so the (now fixed) refs disappear from the list.
      runPreview();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  const downloadBackup = async (url, filename) => {
    try {
      const r = await api.get(url.replace(/^\/api/, ""), {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bucketRows = (bucket) =>
    (preview?.records || []).filter((r) => r.bucket === bucket);

  return (
    <Layout>
      <Toaster position="bottom-right" richColors />
      <div className="max-w-6xl mx-auto">
        <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
          Super Admin
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320]">
          Workflow Repair
        </h1>
        <p className="text-sm text-[#4a524c] mt-1 mb-4">
          Scan for forms stuck from the pre-Phase-24 auto-approve bugs and
          repair them in place. Preview changes nothing. Apply writes to the
          database and creates a JSON backup before every change.
        </p>

        <div
          className="rounded-xl border-2 border-[#7a1f2b] bg-[#fbeae8] p-4 flex items-start gap-3 text-sm text-[#5a1520] mb-6"
          data-testid="workflow-repair-warning"
        >
          <Shield size={18} className="shrink-0 mt-0.5" />
          <div>
            <strong>Super Admins only.</strong> Preview is read-only. Apply
            modifies the live database. A backup JSON is created automatically
            before any changes — download it from the result banner.
            Attachments, audit trail entries, and form data are preserved
            on every repair.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button
            data-testid="workflow-repair-preview-btn"
            onClick={runPreview}
            disabled={previewLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-[#2d6b6b] hover:bg-[#1f5151] text-white px-5 py-2.5 font-semibold shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {previewLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Play size={16} />
            )}
            {previewLoading ? "Scanning…" : "Preview"}
          </button>

          <button
            data-testid="workflow-repair-apply-btn"
            onClick={runApply}
            disabled={
              applying || !preview || !confirmed || tickedActionableCount() === 0
            }
            className="inline-flex items-center gap-2 rounded-lg bg-[#7a1f2b] hover:bg-[#5a1520] text-white px-5 py-2.5 font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {applying ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Rocket size={16} />
            )}
            {applying
              ? "Applying…"
              : `Apply ${tickedActionableCount()} change(s)`}
          </button>

          {preview && (
            <div className="text-xs text-[#4a524c] ml-auto">
              Preview run at:{" "}
              <span className="font-mono">{preview.preview_ran_at}</span>
            </div>
          )}
        </div>

        {applyResult && (
          <div
            className="rounded-xl border-2 border-[#2d6b6b] bg-[#f1faf6] p-4 mb-6"
            data-testid="workflow-repair-result-banner"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2
                size={20}
                className="text-[#1f5151] shrink-0 mt-0.5"
              />
              <div className="flex-1">
                <div className="font-semibold text-[#1f3f36] mb-1">
                  Repair applied by {applyResult.applied_by}
                </div>
                <div className="text-xs text-[#4a524c] mb-3 font-mono">
                  {applyResult.applied_at}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div className="rounded-md bg-white border border-[#dcefe6] p-3">
                    <div className="text-xs uppercase tracking-wider text-[#2d6b6b] font-semibold mb-1">
                      Bucket A
                    </div>
                    <div>
                      Applied:{" "}
                      <strong>
                        {applyResult.results.bucket_a.applied.length}
                      </strong>{" "}
                      / {applyResult.results.bucket_a.total}
                    </div>
                    {applyResult.results.bucket_a.skipped.length > 0 && (
                      <div className="text-xs text-[#8a6b2b] mt-1">
                        Skipped: {applyResult.results.bucket_a.skipped.length}
                      </div>
                    )}
                  </div>
                  <div className="rounded-md bg-white border border-[#dcefe6] p-3">
                    <div className="text-xs uppercase tracking-wider text-[#2d6b6b] font-semibold mb-1">
                      Bucket B
                    </div>
                    <div>
                      Applied:{" "}
                      <strong>
                        {applyResult.results.bucket_b.applied.length}
                      </strong>{" "}
                      / {applyResult.results.bucket_b.total}
                    </div>
                    {applyResult.results.bucket_b.skipped.length > 0 && (
                      <div className="text-xs text-[#8a6b2b] mt-1">
                        Skipped: {applyResult.results.bucket_b.skipped.length}
                      </div>
                    )}
                  </div>
                  <div className="rounded-md bg-white border border-[#dcefe6] p-3">
                    <div className="text-xs uppercase tracking-wider text-[#2d6b6b] font-semibold mb-1">
                      Bucket C
                    </div>
                    <div>
                      Skipped (manual):{" "}
                      <strong>
                        {applyResult.results.bucket_c.skipped_manual_review.length}
                      </strong>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    data-testid="workflow-repair-backup-download"
                    onClick={() =>
                      downloadBackup(
                        applyResult.backup_download_url,
                        applyResult.backup_file,
                      )
                    }
                    className="inline-flex items-center gap-2 rounded-md border border-[#2d6b6b] text-[#1f5151] bg-white hover:bg-[#e7f5ef] px-3 py-1.5 text-sm font-semibold"
                  >
                    <Download size={14} /> Download backup — {applyResult.backup_file}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {preview && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
              <SummaryTile
                label="Bucket A"
                count={preview.counts.bucket_a}
                tint="bg-[#dcefe6] text-[#1f3f36]"
              />
              <SummaryTile
                label="Bucket B"
                count={preview.counts.bucket_b}
                tint="bg-[#f1e2bd] text-[#5a4215]"
              />
              <SummaryTile
                label="Bucket C"
                count={preview.counts.bucket_c}
                tint="bg-[#f1c7c1] text-[#5a1013]"
              />
              <SummaryTile
                label="Total open"
                count={preview.counts.total}
                tint="bg-white border border-[#e2d7c3] text-[#1f2320]"
              />
            </div>

            {(["A", "B", "C"]).map((b) => {
              const rows = bucketRows(b);
              const meta = BUCKET_META[b];
              return (
                <section
                  key={b}
                  data-testid={`workflow-repair-bucket-${b.toLowerCase()}`}
                  className={`rounded-xl border-2 ${meta.tint} mb-4`}
                >
                  <button
                    onClick={() =>
                      setOpenBucket({ ...openBucket, [b]: !openBucket[b] })
                    }
                    className="w-full flex items-center gap-2 px-4 py-3 text-left"
                  >
                    {openBucket[b] ? (
                      <ChevronDown size={18} />
                    ) : (
                      <ChevronRight size={18} />
                    )}
                    <span className="font-semibold text-[#1f2320]">
                      {meta.title}
                    </span>
                    <span
                      className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${meta.chip}`}
                    >
                      {rows.length}
                    </span>
                  </button>
                  {openBucket[b] && (
                    <div className="px-4 pb-4">
                      <p className="text-xs text-[#4a524c] mb-2">
                        {meta.describe}
                      </p>
                      {rows.length === 0 ? (
                        <div className="text-sm text-[#4a524c] italic py-3">
                          No records in this bucket.
                        </div>
                      ) : (
                        <RepairTable
                          bucket={b}
                          rows={rows}
                          actionable={meta.actionable}
                          ticked={ticked}
                          setTicked={setTicked}
                        />
                      )}
                    </div>
                  )}
                </section>
              );
            })}

            <label className="flex items-center gap-2 bg-white border border-[#e2d7c3] rounded-xl p-3 mb-3 text-sm text-[#1f2320]">
              <input
                data-testid="workflow-repair-confirm-checkbox"
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="h-4 w-4 accent-[#7a1f2b]"
              />
              <span>
                I have reviewed the preview and want to apply the ticked
                changes. A JSON backup will be created automatically before
                any write.
              </span>
            </label>

            {preview.counts.total === 0 && (
              <div
                className="flex items-center gap-2 text-sm text-[#1f5151] bg-[#f1faf6] border border-[#dcefe6] rounded-md p-3"
                data-testid="workflow-repair-empty"
              >
                <CheckCircle2 size={16} />
                All open requisitions look healthy — no repair needed.
              </div>
            )}
          </>
        )}

        {!preview && (
          <div
            className="rounded-xl border border-dashed border-[#e2d7c3] bg-white p-8 text-center"
            data-testid="workflow-repair-empty-prompt"
          >
            <AlertTriangle
              size={28}
              className="mx-auto text-[#b48a45] mb-2"
            />
            <p className="text-sm text-[#4a524c]">
              Click <strong>Preview</strong> to scan the current database. No
              changes are made until you review and click Apply.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}

function SummaryTile({ label, count, tint }) {
  return (
    <div className={`rounded-xl p-3 ${tint}`}>
      <div className="text-xs uppercase tracking-wider font-semibold mb-1 opacity-80">
        {label}
      </div>
      <div className="text-2xl font-bold">{count}</div>
    </div>
  );
}

function RepairTable({ bucket, rows, actionable, ticked, setTicked }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs sm:text-sm">
        <thead>
          <tr className="text-left text-[#4a524c] border-b border-[#e2d7c3]">
            {actionable && <th className="px-2 py-2 w-10"></th>}
            <th className="px-2 py-2">Ref</th>
            <th className="px-2 py-2">Form / Version</th>
            <th className="px-2 py-2">Current Stage</th>
            <th className="px-2 py-2">Current Approvers</th>
            <th className="px-2 py-2">Proposed Stage</th>
            <th className="px-2 py-2">Proposed Approvers</th>
            <th className="px-2 py-2">Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.ref_code}
              className={
                actionable
                  ? "border-b border-[#efe7d4]"
                  : "border-b border-[#efe7d4] bg-[#faf3f2] opacity-80"
              }
            >
              {actionable && (
                <td className="px-2 py-2">
                  <input
                    type="checkbox"
                    data-testid={`workflow-repair-row-${r.ref_code}`}
                    checked={!!ticked[r.ref_code]}
                    onChange={(e) =>
                      setTicked({ ...ticked, [r.ref_code]: e.target.checked })
                    }
                    className="h-4 w-4 accent-[#7a1f2b]"
                  />
                </td>
              )}
              <td className="px-2 py-2 font-mono">
                <a
                  href={`/requisitions/${r.ref_code}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#7a1f2b] hover:underline"
                >
                  {r.ref_code}
                </a>
              </td>
              <td className="px-2 py-2">
                {r.form_type} · v{r.workflow_version || "—"}
              </td>
              <td className="px-2 py-2 font-mono text-[11px]">
                {r.current_stage}
              </td>
              <td className="px-2 py-2 text-[11px]">
                {(r.current_approvers || []).join(", ") || "—"}
              </td>
              <td className="px-2 py-2 font-mono text-[11px]">
                {r.proposed_stage || "—"}
              </td>
              <td className="px-2 py-2 text-[11px]">
                {(r.proposed_approvers || []).join(", ") || "—"}
              </td>
              <td className="px-2 py-2 text-[11px] text-[#4a524c]">
                {r.note}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
